// src/.../DebtConsolidationCalculator/Hooks/useDebtConsolidationMath.js
// Update: currentMortgage supports SIMPLE inputs (apr/monthly/term) OR STEPPED (initialApr/svrApr/initialPeriod)

import { useMemo } from "react";

/** ---------- helpers ---------- */
const parseNum = (v) => {
  if (v === "" || v == null) return NaN;
  if (typeof v === "number") return v;
  const s = String(v).replace(/,/g, "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

export const clampNum = (v) => {
  const n = parseNum(v);
  return Number.isFinite(n) ? n : 0;
};

export const aprToMonthly = (aprPct) => clampNum(aprPct) / 100 / 12;

/** PMT: payment for principal L, rate r (per month), term n (months) */
export function pmt(L, r, n) {
  L = clampNum(L);
  r = clampNum(r);
  n = clampNum(n);
  if (L <= 0 || n <= 0) return 0;
  if (r === 0) return L / n;
  const a = Math.pow(1 + r, n);
  return (L * r * a) / (a - 1);
}

/** Solve months for payment P on balance L at monthly rate r */
export function periodsForPayment(L, r, P) {
  L = clampNum(L);
  r = clampNum(r);
  P = clampNum(P);
  if (L <= 0 || P <= 0) return 0;
  if (r === 0) return L / P;
  if (P <= r * L) return Infinity; // negative amortization
  const num = Math.log(P) - Math.log(P - r * L);
  const den = Math.log(1 + r);
  return num / den;
}

/** -------- amortization helpers (monthly -> annual table) -------- */
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
function amortizeFixedPaymentMonthly({
  principal,
  aprPct,
  monthlyPayment,
  maxMonths = 1200,
  note = "",
}) {
  let bal = clampNum(principal);
  const r = clampNum(aprPct) > 0 ? aprToMonthly(aprPct) : 0;
  const pay = clampNum(monthlyPayment);

  const months = [];
  if (bal <= 0 || pay <= 0) return { months, capped: false };

  let capped = false;

  for (let m = 1; m <= maxMonths; m++) {
    const interestRaw = bal * r;
    const interest = round2(interestRaw);

    // negative amortization guard
    if (r > 0 && pay <= interest + 1e-9) {
      capped = true;
      months.push({
        month: m,
        openingBalance: round2(bal),
        payment: round2(pay),
        interest,
        principal: 0,
        closingBalance: round2(bal),
        aprPct,
        note: note || "Payment <= interest (never amortises)",
      });
      break;
    }

    // principal based on rounded interest
    let principalPaid = round2(pay - interest);
    principalPaid = Math.max(0, principalPaid);
    principalPaid = Math.min(round2(bal), principalPaid);

    // final payment adjusted
    const actualPay = round2(principalPaid + interest);

    const closing = round2(Math.max(0, bal - principalPaid));

    months.push({
      month: m,
      openingBalance: round2(bal),
      payment: actualPay,
      interest,
      principal: principalPaid,
      closingBalance: closing,
      aprPct,
      note,
    });

    bal = closing;
    if (bal <= 0) break;
  }
  if (bal > 1e-6 && months.length >= maxMonths) capped = true;

  return { months, capped };
}

function amortizeTermMonthly({ principal, aprPct, termMonths, note = "" }) {
  const L = clampNum(principal);
  const n = clampNum(termMonths);
  const r = clampNum(aprPct) > 0 ? aprToMonthly(aprPct) : 0;
  if (L <= 0 || n <= 0) return { months: [], capped: false };

  const pay = pmt(L, r, n);
  return amortizeFixedPaymentMonthly({
    principal: L,
    aprPct,
    monthlyPayment: pay,
    maxMonths: n,
    note,
  });
}

function mergeMonthlyStreams(streams) {
  const maxLen = Math.max(0, ...streams.map((s) => s.months.length));
  const out = [];

  for (let i = 0; i < maxLen; i++) {
    const rows = streams.map((s) => s.months[i]).filter(Boolean);
    if (rows.length === 0) continue;

    const openingBalance = rows.reduce((a, r) => a + (r.openingBalance || 0), 0);

    out.push({
      month: i + 1,
      openingBalance,
      payment: rows.reduce((a, r) => a + (r.payment || 0), 0),
      interest: rows.reduce((a, r) => a + (r.interest || 0), 0),
      principal: rows.reduce((a, r) => a + (r.principal || 0), 0),
      closingBalance: rows.reduce((a, r) => a + (r.closingBalance || 0), 0),
      aprPct:
        openingBalance > 0
          ? rows.reduce(
              (a, r) =>
                a + clampNum(r.aprPct) * (r.openingBalance || 0),
              0,
            ) / openingBalance
          : 0,
      note: "Merged",
    });
  }

  return out;
}

function monthlyToAnnualTable(months, { maxYears = 100 } = {}) {
  if (!months || months.length === 0) return [];

  const out = [];
  const totalMonths = months.length;
  const totalYears = Math.min(maxYears, Math.ceil(totalMonths / 12));

  for (let y = 1; y <= totalYears; y++) {
    const startIdx = (y - 1) * 12;
    const endIdx = Math.min(y * 12, totalMonths);
    const slice = months.slice(startIdx, endIdx);
    if (slice.length === 0) continue;

    const opening = slice[0].openingBalance || 0;
    const closing = slice[slice.length - 1].closingBalance || 0;

    const totalPayments = slice.reduce((a, r) => a + (r.payment || 0), 0);
    const interestPaid = slice.reduce((a, r) => a + (r.interest || 0), 0);
    const principalPaid = slice.reduce((a, r) => a + (r.principal || 0), 0);

    const denom = slice.reduce((a, r) => a + (r.openingBalance || 0), 0);
    const avgAprPct =
      denom > 0
        ? slice.reduce(
            (a, r) => a + clampNum(r.aprPct) * (r.openingBalance || 0),
            0,
          ) / denom
        : 0;

    out.push({
      year: y,
      openingBalance: opening,
      totalPayments,
      interestPaid,
      principalPaid,
      closingBalance: closing,
      avgAprPct,
      notes: endIdx < y * 12 ? "Paid off mid-year" : "",
    });
  }

  return out;
}

/** UI-aligned revolving labels */
export const REVOLVING_TYPES = [
  "Credit Card",
  "Store Card",
  "Overdraft",
  "Other",
];

/** Only credit cards enforce a 1% minimum payment */
const effectiveRevolvingPayment = (label, balance, enteredMonthly) => {
  const bal = clampNum(balance);
  const m = clampNum(enteredMonthly);
  if ((label || "").trim() === "Credit Card") {
    const minPay = bal * 0.01;
    return Math.max(m, minPay);
  }
  return m;
};

/** -------- Current Mortgage: SIMPLE (apr + term OR apr + monthly) -------- */
function calcSimpleMortgage({
  balance = 0,
  apr = 0,
  monthly = 0,
  termYears = 0,
  termMonths = 0,
}) {
  const L = clampNum(balance);
  const aprPct = clampNum(apr);
  const r = aprPct > 0 ? aprToMonthly(aprPct) : 0;

  const nFromTerm = clampNum(termYears) * 12 + clampNum(termMonths);
  const monthlyIn = clampNum(monthly);

  // Respect entered payments; use the term to calculate a payment when absent.
  if (L > 0 && nFromTerm > 0 && monthlyIn <= 0) {
    const m = pmt(L, r, nFromTerm);
    const totalPay = m * nFromTerm;
    return {
      mode: "simple_term",
      monthsTotal: nFromTerm,
      monthlyNow: m,
      monthlyPayInitial: m,
      monthlyPayReversion: 0,
      monthsInitial: nFromTerm,
      monthsReversion: 0,
      totalPay,
      totalInterest: Math.max(0, totalPay - L),
      headlineAprForWeighting: aprPct,
    };
  }

  if (L > 0 && monthlyIn > 0) {
    const mExact = periodsForPayment(L, r, monthlyIn);
    const months = Number.isFinite(mExact) ? Math.ceil(mExact) : Infinity;
    const totalPay = Number.isFinite(months) ? monthlyIn * months : NaN;
    return {
      mode: "simple_monthly",
      monthsTotal: months,
      monthlyNow: monthlyIn,
      monthlyPayInitial: monthlyIn,
      monthlyPayReversion: 0,
      monthsInitial: Number.isFinite(months) ? months : 0,
      monthsReversion: 0,
      totalPay,
      totalInterest: Number.isFinite(totalPay)
        ? Math.max(0, totalPay - L)
        : NaN,
      headlineAprForWeighting: aprPct,
    };
  }

  // Not enough inputs
  return {
    mode: "simple_incomplete",
    monthsTotal: 0,
    monthlyNow: 0,
    monthlyPayInitial: 0,
    monthlyPayReversion: 0,
    monthsInitial: 0,
    monthsReversion: 0,
    totalPay: 0,
    totalInterest: 0,
    headlineAprForWeighting: aprPct || 0,
  };
}

/** -------- Current Mortgage: STEPPED (initialApr -> svrApr) -------- */
/** -------- Current Mortgage: STEPPED (initialApr -> svrApr) -------- */
function calcSteppedMortgageMonthly({
  balance = 0,
  initialApr = 0,
  svrApr = 0,
  initialYears = 0,
  initialMonths = 0,
  termYears = 0,
  termMonths = 0,
  monthly = 0, // ADD: current monthly payment (phase 1)
  svrMonthly = 0, // already exists (phase 2)
}) {
  const L = clampNum(balance);
  const monthsTotal = clampNum(termYears) * 12 + clampNum(termMonths);

  const monthsInitialRaw =
    clampNum(initialYears) * 12 + clampNum(initialMonths);
  const monthsInitial = Math.min(monthsTotal, monthsInitialRaw);
  const monthsReversion = Math.max(0, monthsTotal - monthsInitial);

  const initApr = clampNum(initialApr);
  const svrAprPct = clampNum(svrApr);

  const rInitial = initApr > 0 ? aprToMonthly(initApr) : 0;
  const rSVR = svrAprPct > 0 ? aprToMonthly(svrAprPct) : 0;

  if (L <= 0 || monthsTotal <= 0) {
    return {
      mode: "stepped",
      monthsTotal: 0,
      monthsInitial: 0,
      monthsReversion: 0,
      monthlyPayInitial: 0,
      monthlyPayReversion: 0,
      monthlyNow: 0,
      totalPay: 0,
      totalInterest: 0,
      headlineAprForWeighting: 0,
    };
  }

  // Phase 1 payment: USE ENTERED MONTHLY if provided, else lender-style PMT across full term
  const enteredInitialMonthly = clampNum(monthly);
  const monthlyPayInitial =
    monthsInitial > 0
      ? enteredInitialMonthly > 0
        ? enteredInitialMonthly
        : pmt(L, rInitial, monthsTotal)
      : 0;

  // --- Phase 1 amortisation ---
  const phase1 =
    monthsInitial > 0
      ? amortizeFixedPaymentMonthly({
          principal: L,
          aprPct: initApr,
          monthlyPayment: monthlyPayInitial,
          maxMonths: monthsInitial,
          note: "CM initial",
        })
      : { months: [], capped: false };

  const balanceAfterInitial =
    phase1.months.length > 0
      ? phase1.months[phase1.months.length - 1].closingBalance
      : L;

  // Phase 2 payment: USE ENTERED SVR MONTHLY if provided, else PMT on remaining balance/term
  const enteredSvrMonthly = clampNum(svrMonthly);
  const monthlyPayReversion =
    monthsReversion > 0
      ? enteredSvrMonthly > 0
        ? enteredSvrMonthly
        : pmt(balanceAfterInitial, rSVR, monthsReversion)
      : 0;

  // --- Phase 2 amortisation ---
  const phase2 =
    monthsReversion > 0
      ? amortizeFixedPaymentMonthly({
          principal: balanceAfterInitial,
          aprPct: svrAprPct,
          monthlyPayment: monthlyPayReversion,
          maxMonths: monthsReversion,
          note: "CM SVR",
        })
      : { months: [], capped: false };

  const months = [...phase1.months, ...phase2.months];

  const totalPay = months.reduce((a, m) => a + (m.payment || 0), 0);
  const totalInterest = months.reduce((a, m) => a + (m.interest || 0), 0);

  const monthlyNow =
    monthsInitial > 0 ? monthlyPayInitial : monthlyPayReversion;

  const denom = L * monthsTotal || 1;
  const numer =
    L * initApr * monthsInitial +
    balanceAfterInitial * svrAprPct * monthsReversion;
  const headlineAprForWeighting = numer / denom;

  return {
    mode: "stepped",
    monthsTotal,
    monthsInitial,
    monthsReversion,
    monthlyPayInitial,
    monthlyPayReversion,
    monthlyNow,
    totalPay,
    totalInterest,
    headlineAprForWeighting,
  };
}
/** Decide which current mortgage model to use */
function calcCurrentMortgage(cm) {
  if (!cm) return null;

  const L = clampNum(cm.balance);
  if (L <= 0) return null;

  const hasSteppedSvr = clampNum(cm.svrApr) > 0;
  const hasInitialPeriod =
    clampNum(cm.initialMonths) > 0 || clampNum(cm.initialYears) > 0;
  const hasSteppedTerm =
    clampNum(cm.termYears) * 12 + clampNum(cm.termMonths) > 0;
  const hasSteppedSvrPayment = clampNum(cm.svrMonthly) > 0;
  const hasInitialApr = clampNum(cm.initialApr) > 0;

  // STEPPED FIRST (so SVR actually applies)
  if (
    (hasSteppedSvr ||
      hasInitialPeriod ||
      hasSteppedSvrPayment ||
      hasInitialApr) &&
    hasSteppedTerm
  ) {
    return calcSteppedMortgageMonthly({ ...cm, initialApr: cm.initialApr || cm.apr });
  }

  // SIMPLE after
  const hasSimpleTerm =
    clampNum(cm.termYears) * 12 + clampNum(cm.termMonths) > 0;
  const hasSimpleMonthly = clampNum(cm.monthly) > 0;

  if (hasSimpleTerm || hasSimpleMonthly) {
    return calcSimpleMortgage(cm);
  }

  return {
    mode: "incomplete",
    monthsTotal: 0,
    monthsInitial: 0,
    monthsReversion: 0,
    monthlyPayInitial: 0,
    monthlyPayReversion: 0,
    monthlyNow: 0,
    totalPay: 0,
    totalInterest: 0,
    headlineAprForWeighting: 0,
  };
}

/** ---------- hook ---------- */
export default function useDebtConsolidationMath(
  debts = [],
  {
    loanAmount = 0,
    apr = 0,
    termYears = 0,
    termMonths = 0,
    feeMode = "flat",
    feeValue = 0,
    initialApr = 0,
    svrApr = 0,
    initialYears = 0,
    initialMonths = 0,
    initialMonthlyOverride,
    reversionMonthlyOverride,

    // current mortgage input (simple + stepped supported)
    currentMortgage = null,
    currentMortgages,
  } = {},
) {
  const debtsKey = debts
    .map((d) =>
      [
        d?.id,
        d?.label,
        d?.balance,
        d?.monthly,
        d?.plannedMonthly,
        d?.apr,
        d?.termYears,
      ].join("|"),
    )
    .join("||");

  const mortgages = currentMortgages || (currentMortgage ? [currentMortgage] : []);
  const currentMortgageKey = JSON.stringify(mortgages);

 return useMemo(() => {
   /** ----- Existing debts ----- */
   let totalBalance = 0;
   let totalMonthly = 0;
   let weightedAprNumer = 0;

   const perDebtMonths = [];

   // Current mortgage included in "Existing"
   let currentMortgageMonthlyNow = 0;
   let currentMortgageMonths = 0;
   let currentMortgageMode = null;

   /** Totals derived from schedules (not simple monthly*months) */
   let totalPaymentsExistingFromSchedule = 0;
   let totalInterestExistingFromSchedule = 0;

   /** ----- Annual logs (Existing + New) ----- */
   const existingStreams = [];

   mortgages.forEach(currentMortgage => {
     const cm = calcCurrentMortgage(currentMortgage);

     // ✅ Always count the entered mortgage monthly if present
     const enteredMortgageMonthly = clampNum(currentMortgage?.monthly);
     const mortgageBalance = clampNum(currentMortgage?.balance);

     if (cm) {
       currentMortgageMode = cm.mode;
       const mortgageMonthlyNow = enteredMortgageMonthly > 0 ? enteredMortgageMonthly : clampNum(cm.monthlyNow);
       currentMortgageMonthlyNow += mortgageMonthlyNow;
       currentMortgageMonths = Math.max(currentMortgageMonths, cm.monthsTotal || 0);

       // balance & APR weighting only make sense if balance exists
       if (mortgageBalance > 0) {
         totalBalance += mortgageBalance;
         weightedAprNumer +=
           mortgageBalance * clampNum(currentMortgage.apr || currentMortgage.initialApr);
       }

       totalMonthly += mortgageMonthlyNow;

       perDebtMonths.push(cm.monthsTotal);
     } else if (enteredMortgageMonthly > 0) {
       // monthly-only fallback
       currentMortgageMode = "monthly_only";
       currentMortgageMonthlyNow += enteredMortgageMonthly;
       totalMonthly += enteredMortgageMonthly;
     }

     /** Current mortgage stream (only if we can actually simulate it) */
     if (cm && mortgageBalance > 0) {
       const cmBal = mortgageBalance;

       if (
         cm.mode === "simple_term" &&
         Number.isFinite(cm.monthsTotal) &&
         cm.monthsTotal > 0
       ) {
         existingStreams.push(
           amortizeTermMonthly({
             principal: cmBal,
             aprPct: clampNum(cm.headlineAprForWeighting || 0),
             termMonths: cm.monthsTotal,
             note: "Current Mortgage (simple term)",
           }),
         );
       } else if (cm.mode === "simple_monthly" && clampNum(cm.monthlyNow) > 0) {
         existingStreams.push(
           amortizeFixedPaymentMonthly({
             principal: cmBal,
             aprPct: clampNum(cm.headlineAprForWeighting || 0),
             monthlyPayment: clampNum(cm.monthlyNow),
             maxMonths: 1200,
             note: "Current Mortgage (simple monthly)",
           }),
         );
       } else if (
         cm.mode === "stepped" &&
         Number.isFinite(cm.monthsTotal) &&
         cm.monthsTotal > 0
       ) {
         const m0 = clampNum(cm.monthlyPayInitial || 0);
         const m1 = clampNum(cm.monthlyPayReversion || 0);
         const r0 =
           clampNum(currentMortgage?.initialApr) ||
           clampNum(currentMortgage?.apr) ||
           0;
         const r1 = clampNum(currentMortgage?.svrApr) || 0;

         const phase1 = amortizeFixedPaymentMonthly({
           principal: cmBal,
           aprPct: r0,
           monthlyPayment: m0 > 0 ? m0 : clampNum(cm.monthlyNow),
           maxMonths: clampNum(cm.monthsInitial || 0) || 0,
           note: "Current Mortgage (initial period)",
         });

       const balAfter1 =
         phase1.months.length > 0
           ? phase1.months[phase1.months.length - 1].closingBalance
           : cmBal;

       const phase2 =
         clampNum(cm.monthsReversion || 0) > 0
           ? amortizeFixedPaymentMonthly({
               principal: balAfter1,
               aprPct: r1,
               monthlyPayment: m1 > 0 ? m1 : clampNum(cm.monthlyNow),
               maxMonths: clampNum(cm.monthsReversion || 0),
               note: "Current Mortgage (SVR period)",
             })
           : { months: [], capped: false };

       existingStreams.push({
         months: [...phase1.months, ...phase2.months],
         capped: phase1.capped || phase2.capped,
       });
     }
   }

   });

   /** Other unsecured debts (✅ monthly always wins; months derived from it) */
   debts.forEach((d) => {
     const bal = clampNum(d?.balance);
     if (bal <= 0) return;

     const aprPct = clampNum(d?.apr);
     const r = aprToMonthly(aprPct);
     const label = (d?.label || "").trim();

     // what user typed in UI
     const userMonthly = clampNum(d?.plannedMonthly);

     let monthly = 0;
     let months = Infinity;

     if (REVOLVING_TYPES.includes(label)) {
       monthly = effectiveRevolvingPayment(label, bal, userMonthly);
       if (monthly > 0) {
         if (aprPct > 0) {
           const mExact = periodsForPayment(bal, r, monthly);
           months = Number.isFinite(mExact) ? Math.ceil(mExact) : Infinity;
         } else {
           months = Math.ceil(bal / monthly);
         }
       }
     } else {
       if (userMonthly > 0) {
         monthly = userMonthly;
         if (aprPct > 0) {
           const mExact = periodsForPayment(bal, r, monthly);
           months = Number.isFinite(mExact) ? Math.ceil(mExact) : Infinity;
         } else {
           months = Math.ceil(bal / monthly);
         }
       } else {
         // fallback only if user didn't enter monthly
         const termN = clampNum(d?.termYears) * 12;
         if (termN > 0) {
           monthly = pmt(bal, r, termN);
           months = termN;
         }
       }
     }

     // Totals
     totalBalance += bal;
     totalMonthly += monthly;
     weightedAprNumer += bal * aprPct;
     perDebtMonths.push(months);

     // Streams (for annual logs + schedule totals)
     if (monthly > 0 && aprPct > 0) {
       existingStreams.push(
         amortizeFixedPaymentMonthly({
           principal: bal,
           aprPct,
           monthlyPayment: monthly,
           maxMonths: Number.isFinite(months) && months > 0 ? months : 1200,
           note: `Existing (${label})`,
         }),
       );
     } else if (monthly > 0 && aprPct === 0) {
       // 0% APR stream: just pay down principal
       existingStreams.push(
         amortizeFixedPaymentMonthly({
           principal: bal,
           aprPct: 0,
           monthlyPayment: monthly,
           maxMonths: Number.isFinite(months) && months > 0 ? months : 1200,
           note: `Existing (${label}) 0%`,
         }),
       );
     }
   });

   const weightedApr = totalBalance > 0 ? weightedAprNumer / totalBalance : 0;

   const finiteMonths = perDebtMonths.filter(
     (m) => Number.isFinite(m) && m > 0,
   );
   const nExisting = finiteMonths.length > 0 ? Math.max(...finiteMonths) : 0;

   const existingMonthlyMerged = mergeMonthlyStreams(existingStreams);
   const annualLogExisting = monthlyToAnnualTable(existingMonthlyMerged);

   // ✅ Schedule-based totals (consistent with annual logs)
   totalPaymentsExistingFromSchedule = existingMonthlyMerged.reduce(
     (a, m) => a + (m.payment || 0),
     0,
   );
   totalInterestExistingFromSchedule = existingMonthlyMerged.reduce(
     (a, m) => a + (m.interest || 0),
     0,
   );

   /** ----- Consolidation mortgage (stepped) ----- */
   const monthsTotal = clampNum(termYears) * 12 + clampNum(termMonths);
   const monthsInitialRaw =
     clampNum(initialYears) * 12 + clampNum(initialMonths);
   const monthsInitial = Math.min(monthsTotal, monthsInitialRaw);
   const monthsReversion = Math.max(0, monthsTotal - monthsInitial);

   const L = clampNum(loanAmount);

   const fee =
     feeMode === "percent"
       ? (L * clampNum(feeValue)) / 100
       : clampNum(feeValue);

   const rInitial = clampNum(initialApr) > 0 ? aprToMonthly(initialApr) : 0;
   const rSVR = clampNum(svrApr) > 0 ? aprToMonthly(svrApr) : aprToMonthly(apr);

   let monthlyPayInitialCalc = 0;
   let monthlyPayReversionCalc = 0;

   if (monthsTotal > 0 && L > 0) {
     if (monthsInitial > 0) {
       monthlyPayInitialCalc = pmt(L, rInitial, monthsTotal);

       let balanceAfterInitial;
       if (rInitial === 0) {
         balanceAfterInitial = Math.max(
           0,
           L - monthlyPayInitialCalc * monthsInitial,
         );
       } else {
         const a = Math.pow(1 + rInitial, monthsInitial);
         balanceAfterInitial =
           L * a - monthlyPayInitialCalc * ((a - 1) / rInitial);
       }

       const L2 = Math.max(0, balanceAfterInitial);
       if (monthsReversion > 0)
         monthlyPayReversionCalc = pmt(L2, rSVR, monthsReversion);
     } else {
       monthlyPayReversionCalc = pmt(L, rSVR, monthsTotal);
     }
   }

   const m0 =
     clampNum(initialMonthlyOverride) > 0
       ? clampNum(initialMonthlyOverride)
       : monthlyPayInitialCalc;
   const m1 =
     clampNum(reversionMonthlyOverride) > 0
       ? clampNum(reversionMonthlyOverride)
       : monthlyPayReversionCalc;

   const monthlyPayInitial = monthsInitial > 0 ? m0 : 0;
   const monthlyPayReversion = monthsReversion > 0 ? m1 : 0;
   const monthlyPayNew = monthlyPayInitial || monthlyPayReversion;

   const upfrontCash = L - fee;
   const shortfall = Math.max(0, totalBalance - upfrontCash);
   const surplus = Math.max(0, upfrontCash - totalBalance);

   const anyNeverPays = perDebtMonths.some((m) => m === Infinity);
   const warnNeverPaysOff =
     anyNeverPays ||
     (!Number.isFinite(nExisting) && totalBalance > 0 && totalMonthly > 0);

   /** New consolidation mortgage schedule */
   const newMonths = [];
   if (monthsTotal > 0 && L > 0) {
     const p1 =
       monthsInitial > 0
         ? amortizeFixedPaymentMonthly({
             principal: L,
             aprPct: clampNum(initialApr),
             monthlyPayment: monthlyPayInitial,
             maxMonths: monthsInitial,
             note: "New Mortgage (initial)",
           })
         : { months: [], capped: false };

     const balAfter = p1.months.length
       ? p1.months[p1.months.length - 1].closingBalance
       : L;

     const p2 =
       monthsReversion > 0
         ? amortizeFixedPaymentMonthly({
             principal: balAfter,
             aprPct: clampNum(svrApr) || clampNum(apr),
             monthlyPayment: monthlyPayReversion,
             maxMonths: monthsReversion,
             note: "New Mortgage (SVR)",
           })
         : { months: [], capped: false };

     newMonths.push(...p1.months, ...p2.months);
   }

   const totalPaymentsNew = newMonths.length
     ? newMonths.reduce((a, m) => a + (m.payment || 0), 0)
     : NaN;

   const totalInterestNew = newMonths.length
     ? newMonths.reduce((a, m) => a + (m.interest || 0), 0)
     : NaN;

   const annualLogNew = monthlyToAnnualTable(newMonths);

   return {
     // Existing
     totalBalance,
     totalMonthly,
     weightedApr,
     nExisting,
     totalPaymentsExisting: totalPaymentsExistingFromSchedule,
     totalInterestExisting: totalInterestExistingFromSchedule,
     annualLogExisting,
     annualLogNew,

     // current mortgage details (optional for UI)
     currentMortgageMonthlyNow,
     currentMortgageMonths,
     currentMortgageMode,

     // New
     months: monthsTotal,
     fee,
     monthlyPayInitial,
     monthlyPayReversion,
     monthlyPayNew,
     totalPaymentsNew,
     totalInterestNew,

     // Cash flow
     upfrontCash,
     shortfall,
     surplus,

     warnNeverPaysOff,
   };
 }, [
   debtsKey,
   currentMortgageKey,
   loanAmount,
   apr,
   termYears,
   termMonths,
   feeMode,
   feeValue,
   initialApr,
   svrApr,
   initialYears,
   initialMonths,
   initialMonthlyOverride,
   reversionMonthlyOverride,
 ]);
}
